import { useState, useEffect, useCallback, type FC, type ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { WelcomeTour } from '@/components/onboarding/WelcomeTour';
import { usePageTour } from '@/hooks/usePageTour';
import { WELCOME_TOUR_ID } from '@/lib/tour-data/welcome';

interface AppLayoutProps {
  children: ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });

  const welcomeTour = usePageTour(WELCOME_TOUR_ID);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  // Afficher le welcome tour a la premiere connexion
  useEffect(() => {
    if (welcomeTour.isActive) {
      setWelcomeOpen(true);
    }
  }, [welcomeTour.isActive]);

  const handleCloseWelcome = useCallback(() => {
    setWelcomeOpen(false);
    welcomeTour.completeTour();
  }, [welcomeTour]);

  const handleRestartWelcome = useCallback(() => {
    setWelcomeOpen(true);
  }, []);

  // Auto-collapse sur mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex h-screen flex-col bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      <Header onRestartWelcomeTour={handleRestartWelcome} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
      <WelcomeTour isOpen={welcomeOpen} onClose={handleCloseWelcome} />
    </div>
  );
};
